/**
 * OAuth callback handler
 * Processes OAuth tokens from URL or Electron IPC.
 *
 * Supports two flows:
 * 1. Legacy: accessToken + refreshToken in URL params (old OAuth)
 * 2. PKCE: code + state in URL params → exchange for tokens (new cloud OAuth)
 *
 * Called from:
 * 1. Web: window.location when redirected from hub-cloud
 * 2. Electron: IPC message from preload script (app:oauth-callback)
 * 3. Global listener: initGlobalOAuthListener() sets up a persistent IPC
 *    listener that handles Electron callbacks regardless of current page.
 */

import { useAuthStore } from '../stores/auth';
import { isElectron } from '../config/runtime';

let globalListenerInitialized = false;

/**
 * Set up a persistent listener for Electron OAuth callbacks.
 * Call once in App.vue onMounted. Works regardless of which page is active.
 */
export function initGlobalOAuthListener(): void {
  if (!isElectron() || globalListenerInitialized) return;
  globalListenerInitialized = true;

  const authStore = useAuthStore();
  const electron = window.electron;
  if (!electron?.onOAuthCallback) return;

  electron.onOAuthCallback(async (rawUrl: string) => {
    console.log('[OAuth] Global listener received callback:', rawUrl);
    try {
      const url = new URL(rawUrl.startsWith('http') ? rawUrl : rawUrl.replace(/^otto:\/\//, 'https://torik.ai/'));
      const code = url.searchParams.get('code');
      const state = url.searchParams.get('state');

      if (code && state) {
        console.log('[OAuth] PKCE flow detected — exchanging code for tokens');
        const result = await authStore.loginToCloudViaOAuth(rawUrl);
        if (result.success) {
          console.log('[OAuth] Token exchange successful');
          // Ensure user info is loaded (loginToCloudViaOAuth does this, but be safe)
          await authStore.fetchCloudUser();
        } else {
          console.error('[OAuth] Token exchange failed:', result.error);
        }
        return;
      }

      // Legacy flow: accessToken + refreshToken
      const callbackAccessToken = url.searchParams.get('accessToken') || url.searchParams.get('token');
      const callbackRefreshToken = url.searchParams.get('refreshToken');
      if (callbackAccessToken && callbackRefreshToken) {
        await authStore.handleOAuthCallback(callbackAccessToken, callbackRefreshToken);
        window.location.hash = '#/dashboard';
      }
    } catch (err) {
      console.error('[OAuth] Global listener error:', err);
    }
  });

  console.log('[OAuth] Global IPC listener initialized');
}

/**
 * Process OAuth callback
 */
export async function handleOAuthCallback(): Promise<boolean> {
  const authStore = useAuthStore();

  // Try to get tokens from URL params (web)
  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('accessToken');
  const refreshToken = params.get('refreshToken');

  if (accessToken && refreshToken) {
    console.log('[OAuth] Processing legacy callback from URL params');
    return authStore.handleOAuthCallback(accessToken, refreshToken);
  }

  // If running in Electron, listen for IPC callback
  if (isElectron()) {
    return new Promise((resolve) => {
      window.electron?.onOAuthCallback(async (url: string) => {
        console.log('[OAuth] Processing callback from Electron IPC:', url);

        try {
          // Parse the redirect URL — supports otto:// and hub:// schemes
          const normalizedUrl = url.replace(/^otto:\/\//, 'https://torik.ai/').replace(/^hub:\/\//, 'https://');

          const callbackUrl = new URL(normalizedUrl);
          const code = callbackUrl.searchParams.get('code');
          const state = callbackUrl.searchParams.get('state');

          // ── New PKCE flow: code + state → exchange for tokens ─────
          if (code && state) {
            console.log('[OAuth] PKCE flow detected via Electron IPC');
            const result = await authStore.loginToCloudViaOAuth(url);
            resolve(result.success);
            return;
          }

          // ── Legacy flow: accessToken + refreshToken in URL ────────
          const callbackAccessToken =
            callbackUrl.searchParams.get('accessToken') || callbackUrl.searchParams.get('token');
          const callbackRefreshToken = callbackUrl.searchParams.get('refreshToken');

          if (callbackAccessToken && callbackRefreshToken) {
            const success = await authStore.handleOAuthCallback(callbackAccessToken, callbackRefreshToken);
            resolve(success);
          } else {
            console.error('[OAuth] Unrecognized callback params in URL:', url);
            resolve(false);
          }
        } catch (error) {
          console.error('[OAuth] Failed to parse callback URL:', error);
          resolve(false);
        }
      });
    });
  }

  return false;
}
